/**
 * Auth Store
 * Manages authentication state, JWT tokens, and user session
 */

import type { UserDTO, LoginCredentials, RegisterData, AuthResponse } from './types';
import type { UserRole } from './types';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist } from 'zustand/middleware';
import { api } from '@/common/api';
import { isAdminUser } from '@/common/auth/rbac';

/**
 * The web client is an admin-only portal. Regular users (USER / MEMBER) sign in
 * through the mobile app, not here — so every web sign-in path rejects any
 * account that is not an administrator. This gate lives in the client only and
 * intentionally does not touch the shared server auth or the mobile app.
 */
export const ADMIN_ONLY_SIGN_IN_MESSAGE =
  'This portal is for administrators only. Please sign in with an admin account.';

/**
 * Auth store state
 */
export interface AuthState {
  user: UserDTO | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  loginError: string | null;
  registerError: string | null;

  // Actions
  setUser: (user: UserDTO | null) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setAccessToken: (token: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  clearError: () => void;

  // Async actions
  login: (credentials: LoginCredentials) => Promise<UserDTO>;
  register: (data: RegisterData) => Promise<UserDTO>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<string>;
  getCurrentUser: () => Promise<UserDTO>;
  updateProfile: (data: Partial<UserDTO>) => Promise<UserDTO>;
  requestMagicLink: (email: string) => Promise<{ message: string; magicLink: string }>;
  verifyMagicLink: (token: string) => Promise<UserDTO>;

  // Helper methods
  hasRole: (role: string | string[]) => boolean;
  isTokenExpired: () => boolean;
  clearAuthState: () => void;
}

/**
 * Auth store
 */
export const useAuthStore = create<AuthState>()(
  persist(
    immer((set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      loginError: null,
      registerError: null,

      // Sync actions
      setUser: (user) => {
        set((state) => {
          state.user = user;
          state.isAuthenticated = user !== null;
        });
      },

      setTokens: (accessToken, refreshToken) => {
        set((state) => {
          state.accessToken = accessToken;
          state.refreshToken = refreshToken;
          state.isAuthenticated = true;
        });
      },

      setAccessToken: (token) => {
        set((state) => {
          state.accessToken = token;
        });
      },

      setLoading: (loading) => {
        set((state) => {
          state.isLoading = loading;
        });
      },

      setError: (error) => {
        set((state) => {
          state.error = error;
        });
      },

      clearError: () => {
        set((state) => {
          state.error = null;
          state.loginError = null;
          state.registerError = null;
        });
      },

      // Async actions
      login: async (credentials) => {
        set((state) => {
          state.isLoading = true;
          state.loginError = null;
        });

        try {
          const response = await api.post<AuthResponse>('/auth/login', credentials);

          if (!isAdminUser(response.user)) {
            throw new Error(ADMIN_ONLY_SIGN_IN_MESSAGE);
          }

          set((state) => {
            state.user = response.user ?? null;
            state.accessToken = response.access_token;
            state.refreshToken = response.refresh_token;
            state.isAuthenticated = true;
            state.isLoading = false;
          });

          return response.user || { id: '', email: '', name: '', role: 'ADMIN' as UserRole, status: 'ACTIVE' as const, createdAt: '', updatedAt: '' };
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Login failed';
          set((state) => {
            state.loginError = message;
            state.isLoading = false;
            state.error = error instanceof Error ? error : new Error(message);
          });
          throw error;
        }
      },

      register: async (data) => {
        set((state) => {
          state.isLoading = true;
          state.registerError = null;
        });

        try {
          const response = await api.post<AuthResponse>('/auth/register', data);

          if (!isAdminUser(response.user)) {
            throw new Error(ADMIN_ONLY_SIGN_IN_MESSAGE);
          }

          set((state) => {
            state.user = response.user ?? null;
            state.accessToken = response.access_token;
            state.refreshToken = response.refresh_token;
            state.isAuthenticated = true;
            state.isLoading = false;
          });

          return response.user || { id: '', email: '', name: '', role: 'ADMIN' as UserRole, status: 'ACTIVE' as const, createdAt: '', updatedAt: '' };
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Registration failed';
          set((state) => {
            state.registerError = message;
            state.isLoading = false;
            state.error = error instanceof Error ? error : new Error(message);
          });
          throw error;
        }
      },

      logout: async () => {
        // Capture token then wipe storage immediately so no in-flight request
        // can use the old refresh token after this point.
        const currentToken = get().accessToken;
        localStorage.removeItem('auth-store');
        set((state) => {
          state.user = null;
          state.accessToken = null;
          state.refreshToken = null;
          state.isAuthenticated = false;
          state.isLoading = false;
          state.error = null;
          state.loginError = null;
          state.registerError = null;
        });

        // Best-effort server-side session invalidation with the captured token.
        try {
          await fetch(
            `${(import.meta.env.VITE_API_BASE_URL as string | undefined) || 'http://localhost:3000'}/auth/logout`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(currentToken ? { Authorization: `Bearer ${currentToken}` } : {}),
              },
            }
          );
        } catch {
          // not critical — tokens are already cleared client-side
        }
      },

      refreshAccessToken: async () => {
        const state = get();
        if (!state.refreshToken) {
          throw new Error('No refresh token available');
        }

        try {
          const response = await api.post<{ access_token: string }>(
            '/auth/refresh',
            {
              refresh_token: state.refreshToken,
            }
          );

          set((s) => {
            s.accessToken = response.access_token;
          });

          return response.access_token;
        } catch (error) {
          // If refresh fails, clear auth state
          set((s) => {
            s.user = null;
            s.accessToken = null;
            s.refreshToken = null;
            s.isAuthenticated = false;
            s.error = error instanceof Error ? error : new Error('Token refresh failed');
          });
          throw error;
        }
      },

      getCurrentUser: async () => {
        set((state) => {
          state.isLoading = true;
        });

        try {
          const user = await api.get<UserDTO>('/auth/profile');

          if (!isAdminUser(user)) {
            // Admin-only portal: a non-admin (or newly demoted) account must not
            // keep a restored session. Throwing here lets useAuthInit clear it.
            throw new Error(ADMIN_ONLY_SIGN_IN_MESSAGE);
          }

          set((state) => {
            state.user = user;
            state.isLoading = false;
          });

          return user;
        } catch (error) {
          set((state) => {
            state.isLoading = false;
            state.error = error instanceof Error ? error : new Error('Failed to fetch user');
          });
          throw error;
        }
      },

      updateProfile: async (data) => {
        set((state) => {
          state.isLoading = true;
        });

        try {
          const updated = await api.patch<UserDTO>('/auth/profile', data);

          set((state) => {
            state.user = updated;
            state.isLoading = false;
          });

          return updated;
        } catch (error) {
          set((state) => {
            state.isLoading = false;
            state.error = error instanceof Error ? error : new Error('Profile update failed');
          });
          throw error;
        }
      },

      requestMagicLink: async (email) => {
        set((state) => {
          state.isLoading = true;
          state.loginError = null;
        });

        try {
          const response = await api.post<{ message: string; magicLink: string }>(
            '/auth/magic-login',
            { email }
          );

          set((state) => {
            state.isLoading = false;
          });

          return response;
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to request magic link';
          set((state) => {
            state.loginError = message;
            state.isLoading = false;
            state.error = error instanceof Error ? error : new Error(message);
          });
          throw error;
        }
      },

      verifyMagicLink: async (token) => {
        set((state) => {
          state.isLoading = true;
          state.loginError = null;
        });

        try {
          const response = await api.get<AuthResponse>(`/auth/magic-login/callback?token=${token}`);

          if (!isAdminUser(response.user)) {
            throw new Error(ADMIN_ONLY_SIGN_IN_MESSAGE);
          }

          set((state) => {
            state.user = response.user ?? null;
            state.accessToken = response.access_token;
            state.refreshToken = response.refresh_token;
            state.isAuthenticated = true;
            state.isLoading = false;
          });

          return response.user || { id: '', email: '', name: '', role: 'ADMIN' as UserRole, status: 'ACTIVE' as const, createdAt: '', updatedAt: '' };
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Magic link verification failed';
          set((state) => {
            state.loginError = message;
            state.isLoading = false;
            state.error = error instanceof Error ? error : new Error(message);
          });
          throw error;
        }
      },

      // Helper methods
      hasRole: (role) => {
        const state = get();
        if (!state.user) return false;

        const roles = Array.isArray(role) ? role : [role];
        return roles.includes(state.user.role);
      },

      isTokenExpired: () => {
        const state = get();
        if (!state.accessToken) return true;

        try {
          // Decode JWT token (without verification)
          const parts = state.accessToken.split('.');
          if (parts.length !== 3) return true;

          const decoded = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))) as {
            exp?: number;
          };

          if (!decoded.exp) return true;

          // Token expires 1 minute before actual expiration
          return Date.now() >= (decoded.exp - 60) * 1000;
        } catch {
          return true;
        }
      },

      clearAuthState: () => {
        localStorage.removeItem('auth-store');
        set((state) => {
          state.user = null;
          state.accessToken = null;
          state.refreshToken = null;
          state.isAuthenticated = false;
          state.isLoading = false;
          state.error = null;
          state.loginError = null;
          state.registerError = null;
        });
      },
    })),
    {
      name: 'auth-store',
      version: 1,
      // Persist these fields
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      // No rehydration logging here: `state` carries the access and refresh
      // tokens, and logging it printed a working admin session into the browser
      // console (and into any error-reporting tool that captures console output)
      // on every page load.
    }
  )
);

/**
 * Auth store selectors
 */
export const useAuthUser = () => useAuthStore((state) => state.user);
export const useAuthTokens = () =>
  useAuthStore((state) => ({
    accessToken: state.accessToken,
    refreshToken: state.refreshToken,
  }));
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () =>
  useAuthStore((state) => ({
    error: state.error,
    loginError: state.loginError,
    registerError: state.registerError,
  }));
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
